import classNames from "classnames/bind";
import React, { useMemo, useRef } from "react";
import { DayPicker } from "react-day-picker";
import FontAwesome from "react-fontawesome";
import overlayStyles from "../Form/FormInputs/DateInput/DateOverlay.module.scss";
import useOnClickOutside from "../utils/hooks/useOnClickOutside";
import { getTimeStringFromDate } from "../utils/timeFormatter";
import CalendarFooter from "./CalendarFooter";
import CalendarTimePicker from "./CalendarTimePicker";
import CustomDateRange from "./CustomDateRange";
import styles from "./DateRangeSelector.module.scss";
import {
  getActiveCustomDateRange,
  getDefaultCustomDateRange,
} from "./DateRangeSelectorHelper";
import { DateRangeSelectorProps } from "./types";
import useDateRangeInput from "./useDateRangeInput";

const cx = classNames.bind(styles);
const overlayCx = classNames.bind(overlayStyles);

/**
 * A standalone input that takes in a range of days
 * @deprecated use "DateSelector" instead (see Storybook documentation)
 */
const DateRangeSelector = ({
  value = { from: undefined, to: undefined },
  onBlur,
  onChange,
  isDisabled = false,
  icon = true,
  isUTC = false,
  enableCustomDateRange = true,
  customDateRange,
  allowTimeSelection,
  allowOneDayRange,
  disabledDays,
  disableFrom,
  initialMonth = "from",
  hoverRange = "daily",
  allowMultipleHoverRange,
}: DateRangeSelectorProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const {
    fromInputProps,
    toInputProps,
    customRangeProps,
    dayPickerProps,
    popoverProps,
    footerProps,
    timeProps,
  } = useDateRangeInput({
    // @ts-expect-error - This is not required, we'll need to update a lot of types to support this being optional in TS
    value,
    onChange,
    onBlur,
    allowTimeSelection,
    allowOneDayRange,
    initialMonth,
    hoverRange,
    allowMultipleHoverRange,
  });

  useOnClickOutside(ref, popoverProps.onClickOutside);

  const availableCustomDateRange = useMemo(() => {
    if (!enableCustomDateRange) {
      return [];
    }

    const allCustomDateRanges =
      customDateRange ?? getDefaultCustomDateRange(new Date(), isUTC);

    return getActiveCustomDateRange(allCustomDateRanges, disabledDays);
  }, [enableCustomDateRange, customDateRange, disabledDays]);

  return (
    <div
      ref={ref}
      className={cx("calendarClickContainer")}
    >
      <div className={cx("container", { active: popoverProps.isOpen })}>
        <input
          {...fromInputProps}
          className={cx("input")}
          disabled={isDisabled || disableFrom}
          autoComplete="off"
          readOnly
        />
        <span className={cx("separator")}>—</span>
        <input
          {...toInputProps}
          className={cx("input")}
          disabled={isDisabled}
          autoComplete="off"
          readOnly
        />
        {icon ? (
          <FontAwesome
            name="calendar"
            className={cx("icon")}
          />
        ) : null}
      </div>

      {popoverProps.isOpen && (
        <div className={overlayCx("overlay")}>
          <div className={cx("overlayContent")}>
            {enableCustomDateRange && hoverRange === "daily" && (
              <CustomDateRange
                {...customRangeProps}
                dateRanges={availableCustomDateRange}
              />
            )}
            <div className={cx("calendarContainer")}>
              <DayPicker
                {...dayPickerProps}
                classNames={overlayStyles}
                modifiersClassNames={overlayStyles}
                mode="range"
                numberOfMonths={2}
                formatters={{
                  formatWeekdayName: (date) =>
                    getTimeStringFromDate(date, "ddd").slice(0, 1),
                }}
                disabled={disabledDays}
              />

              <CalendarFooter {...footerProps} />
            </div>

            {allowTimeSelection && <CalendarTimePicker {...timeProps} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
